import { useState } from "react";
import { useAuth } from "../contexts/auth/AuthContext";
import { useNavigate } from "react-router";
import { AiOutlineBook, AiOutlineBulb, AiOutlineUser } from "react-icons/ai";
import { FiLock } from "react-icons/fi";
import Loading from "./../components/general/Loading";

const LoginView = () => {
  const { authLogin } = useAuth();
  let navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    setLoading(true);
    setErrorMessage("");
    if (email.trim().length <= 0) {
      setErrorMessage("Please fill in a valid login.");
      setLoading(false);
      return;
    }
    if (password.trim().length <= 0) {
      setErrorMessage("Please fill in a password.");
      setLoading(false);
      return;
    }

    authLogin({ email, password })
      .then((res) => {
        navigate("/home");
        setLoading(false);
      })
      .catch((err) => {
        switch (err.response.status) {
          case 400:
            setErrorMessage("Your email/password combination didn't match. Please try again.");
            break;
          case 401:
            setErrorMessage("Your email/password combination didn't match. Please try again.");
            break;
          default:
            setErrorMessage("Something went wrong. Please try again later.");
            break;
        }
        setLoading(false);
      });
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-gradient-to-br from-secondary via-background to-accent/20 px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute -left-24 top-[-7rem] h-72 w-72 rounded-full bg-accent/20 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-link/10 blur-3xl" aria-hidden="true" />

      <div className="relative grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/80 bg-white shadow-large lg:min-h-[620px] lg:grid-cols-[1fr_1.05fr]">
        <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#252638] via-[#34364a] to-[#45485a] p-10 text-white lg:flex">
          <div className="pointer-events-none absolute -right-24 -top-20 h-80 w-80 rounded-full border-[36px] border-white/5" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-28 -left-24 h-80 w-80 rounded-full border-[36px] border-accent/10" aria-hidden="true" />
          <div className="relative z-10">
            <div className="mb-16 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-2xl font-extrabold text-[#252638]">
                S
              </span>
              <span className="font-MS text-xl font-extrabold tracking-wide">StudyLens</span>
            </div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-accent">
              Your learning space
            </p>
            <h1 className="max-w-sm font-MS text-4xl font-bold leading-tight">
              See your learning from a new perspective.
            </h1>
          </div>
          <div className="relative z-10 mt-12 space-y-3">
            <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/15 text-xl text-accent">
                <AiOutlineBulb aria-hidden="true" />
              </span>
              <div>
                <p className="font-semibold">Built at Utrecht University</p>
                <p className="mt-0.5 text-sm text-white/60">
                  Used in research to improve personalized learning experiences
                </p>
              </div>
            </div>
          </div>
        </aside>

        <section className="flex flex-col justify-center px-6 py-8 sm:px-10 lg:px-14">
          <div className="mb-8 lg:hidden">
            <div className="mb-6 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-xl font-extrabold text-txt_primary">
                S
              </span>
              <span className="font-MS text-xl font-extrabold tracking-tight">StudyLens</span>
            </div>
            <p className="text-sm text-txt_primary_light">Your learning space</p>
          </div>
          <div className="mb-7">
            <h2 className="font-MS text-3xl font-extrabold tracking-tight sm:text-4xl">Sign in</h2>
          </div>

          {loading && (
            <div className="mb-2 flex items-center justify-center gap-2 text-sm text-txt_primary_light">
              <Loading />
              <span>Signing you in...</span>
            </div>
          )}
          <div className="mb-3 min-h-6 text-sm text-error" role="alert" aria-live="polite">
            {errorMessage}
          </div>
          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <label htmlFor="login-username" className="flex flex-col gap-2 text-sm font-semibold">
              Username
              <span className="relative">
                <AiOutlineUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-txt_primary_light" aria-hidden="true" />
                <input
                  id="login-username"
                  name="username"
                  className="w-full rounded-xl border border-card_dark bg-white py-3 pl-10 pr-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-accentdark focus:ring-4 focus:ring-accent/20"
                  placeholder="e.g. evo23456"
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </span>
            </label>
            <label htmlFor="login-password" className="flex flex-col gap-2 text-sm font-semibold">
              Password
              <span className="relative">
                <FiLock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-txt_primary_light" aria-hidden="true" />
                <input
                  id="login-password"
                  name="password"
                  className="w-full rounded-xl border border-card_dark bg-white py-3 pl-10 pr-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-accentdark focus:ring-4 focus:ring-accent/20"
                  placeholder="Enter your password"
                  type="password"
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </span>
            </label>
            <button
              type="submit"
              disabled={loading}
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3.5 font-bold text-txt_primary shadow-default transition hover:bg-accentdark focus:outline-none focus:ring-4 focus:ring-accent/30 disabled:cursor-wait disabled:opacity-70"
            >
              {loading ? "Signing in..." : "Sign in"}
              {!loading && <span aria-hidden="true">→</span>}
            </button>
          </form>

          <div className="mt-8 border-t border-card_dark pt-6">
            <div className="rounded-xl bg-secondary px-4 py-3.5 text-sm leading-relaxed text-slate-600">
              <p className="font-semibold text-txt_primary">Need help signing in?</p>
              <p className="mt-1">
                Your teacher provides your username and password. Usernames look like
                {" "}evo23456 or code23456. If you still can’t sign in, contact your teacher.
              </p>
            </div>
            <p className="mt-4 text-center text-sm text-slate-500">
              Noticed an issue?{" "}
              <a
                href="https://forms.office.com/e/WTAp04gTbv"
                className="font-semibold text-link underline decoration-transparent underline-offset-2 transition hover:decoration-current focus:outline-none focus:ring-2 focus:ring-link/40"
                target="_blank"
                rel="noreferrer"
              >
                Report it here
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginView;
